/**
 * ToastViewport — глобальний рендерер toast-повідомлень (Sprint 8b).
 *
 * Дизайн mobile-first:
 * - На mobile: full-width top, easy thumb-reach для X.
 * - На sm+: фіксована ширина у top-right.
 * - z-[10000] — НАД вікнами (у них z-[9998]). Доти тости були z-50 і
 *   ховались під затемнення: «вкажіть причину» у вікні передачі пачки ніхто
 *   не бачив (скарга 26.09.2026).
 *
 * Семантика: role="status" для info/success/warning, role="alert" для error
 * (screen-reader-и інакше пропустять тихі повідомлення).
 *
 * Тост НЕ загороджує вікно (власник 01.10.2026): сам текст пропускає кліки
 * крізь себе — ✕ і «В архів» у шапці вікна під ним натискаються, — а
 * клікабельні лише кнопки тосту (✕, «Скасувати»). Унизу тостам не місце:
 * там «Зберегти» у вікнах і нижні панелі цеху.
 *
 * Без анімацій-бібліотек: Tailwind transition достатньо. Якщо знадобиться
 * swipe-to-dismiss — Sprint 9+, тоді sonner або власний touch-handler.
 */
import { useToastStore, type ToastKind } from "@/store/toast";

const KIND_CLASSES: Record<ToastKind, string> = {
  info:    "bg-blue-50 border-l-4 border-blue-500 text-blue-900",
  success: "bg-green-50 border-l-4 border-mes-success text-green-900",
  warning: "bg-yellow-50 border-l-4 border-mes-warning text-yellow-900",
  error:   "bg-red-50 border-l-4 border-mes-danger text-red-900",
};

const KIND_ROLE: Record<ToastKind, "status" | "alert"> = {
  info: "status",
  success: "status",
  warning: "status",
  error: "alert",
};

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  if (items.length === 0) return null;

  return (
    <div
      className="fixed top-2 left-2 right-2 z-[10000] flex flex-col gap-2 pointer-events-none sm:left-auto sm:right-4 sm:top-4 sm:w-96"
      aria-live="polite"
    >
      {items.map((m) => (
        <div
          key={m.id}
          role={KIND_ROLE[m.kind]}
          // A5: `transition-opacity` тут був мертвий — прозорість ніде не
          // змінюється, тож тост вигулькував миттєво і периферійним зором
          // його не помічали. `toastIn` уже обкатаний у Терміналі.
          className={`pointer-events-none rounded-lg shadow-md p-3 pr-12 relative opacity-95 ${KIND_CLASSES[m.kind]}`}
          style={{ animation: "toastIn 250ms ease-out" }}
        >
          <p className="text-sm font-medium leading-snug break-words">
            {m.text}
          </p>
          {m.action && (
            <button
              type="button"
              onClick={() => {
                dismiss(m.id);
                void m.action?.run();
              }}
              className="pointer-events-auto mt-2 min-h-[44px] px-4 rounded-lg bg-white/80 border border-current/20 text-sm font-bold hover:bg-white"
            >
              {m.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => dismiss(m.id)}
            aria-label="Закрити сповіщення"
            className="pointer-events-auto absolute top-0.5 right-0.5 w-11 h-11 flex items-center justify-center rounded-full hover:bg-black/10 active:bg-black/20 text-current"
          >
            <span aria-hidden="true" className="text-lg leading-none">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
